import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import operatorService, { OperatorStats } from '../../services/operatorService';
import { formatYield, getYieldIconColor } from '../../hooks/useYieldFactor';
import { formatTime as formatDbTime, formatDate as formatDbDate } from '../../utils/dateUtils';
import { getStatusBadgeClass } from '../../utils/statusColors';
import {
  TrendingUp,
  Package,
  Scale,
  Target,
  Trash2,
  Clock,
  Award,
  Calendar,
  BarChart3,
  Activity
} from 'lucide-react';

interface SessionHistory {
  session_number: string;
  status: string;
  product_sku: string;
  product_name: string;
  initial_material_weight: number;
  actual_output_weight: number;
  waste_weight: number;
  yield_percentage: number;
  started_at: string;
  completed_at: string;
  station_name: string;
}

interface PerformanceStats {
  today: OperatorStats;
  week: OperatorStats;
  month: OperatorStats;
}

const PerformanceDashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  usePageTitle(t('operator.performanceDashboard'));
  const [stats, setStats] = useState<PerformanceStats | null>(null);
  const [recentSessions, setRecentSessions] = useState<SessionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('today');

  useEffect(() => {
    if (user?.id) {
      loadPerformanceData();
    }
  }, [user]);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);

      const userId = user!.id;

      // Load stats for all periods
      const [todayStats, weekStats, monthStats, history] = await Promise.all([
        operatorService.getStats(userId, 'today'),
        operatorService.getStats(userId, 'week'),
        operatorService.getStats(userId, 'month'),
        operatorService.getHistory(userId, 10, 0)
      ]);

      setStats({
        today: todayStats,
        week: weekStats,
        month: monthStats
      });

      setRecentSessions(history.sessions || []);
    } catch (error) {
      console.error('Error loading performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStats = (): OperatorStats => {
    if (!stats) {
      return {
        sessions_worked: 0,
        total_output: 0,
        total_waste: 0,
        total_initial_material: 0,
        avg_yield: 0,
        products_produced: 0
      };
    }
    const periodStats = stats[selectedPeriod];
    return {
      sessions_worked: Number(periodStats.sessions_worked) || 0,
      total_output: Number(periodStats.total_output) || 0,
      total_waste: Number(periodStats.total_waste) || 0,
      total_initial_material: Number(periodStats.total_initial_material) || 0,
      avg_yield: Number(periodStats.avg_yield) || 0,
      products_produced: Number(periodStats.products_produced) || 0
    };
  };

  const formatWeight = (weight: number | string | null): string => {
    const numWeight = Number(weight) || 0;
    return `${numWeight.toFixed(2)} lbs`;
  };

  const formatDate = (dateString: string): string => {
    return formatDbDate(dateString);
  };

  const formatTime = (dateString: string): string => {
    return formatDbTime(dateString);
  };

  const calculateSessionDuration = (started: string, completed: string): string => {
    const start = new Date(started);
    const end = new Date(completed);
    const durationMs = end.getTime() - start.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };


  const currentStats = getCurrentStats();
  const wastePercentage = currentStats.total_initial_material > 0 
    ? ((currentStats.total_waste / currentStats.total_initial_material) * 100)
    : 0;

  // Find best session
  const bestSession = recentSessions.reduce((best, session) => {
    if (!best || session.yield_percentage > best.yield_percentage) {
      return session;
    }
    return best;
  }, null as SessionHistory | null);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Activity className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('operator.loadingPerformanceData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-800 dark:to-blue-950 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <p className="text-blue-100">
            {t('operator.welcomeBack')}, {user?.full_name || user?.username}
          </p>
          <Award className="w-12 h-12 opacity-50" />
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('operator.period')}:</span>
          <div className="flex space-x-2 ml-4">
            {(['today', 'week', 'month'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  selectedPeriod === period
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t(`operator.${period}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Sessions Worked */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-300" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('operator.sessions')}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {currentStats.sessions_worked}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('operator.sessionsCompleted')}
          </p>
        </div>

        {/* Total Output */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <Scale className="w-6 h-6 text-green-600 dark:text-green-300" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('operator.output')}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {formatWeight(currentStats.total_output)}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('operator.totalWeightProduced')}
          </p>
        </div>

        {/* Average Yield */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Target className="w-6 h-6 text-purple-600 dark:text-purple-300" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('operator.yield')}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {formatYield(Number(currentStats.avg_yield || 0))}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('operator.averageYieldPercentage')}
          </p>
        </div>

        {/* Total Waste */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-300" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('operator.waste')}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {formatWeight(currentStats.total_waste)}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('operator.wasteRatio')}: {Number(wastePercentage || 0).toFixed(2)}%
          </p>
        </div>

        {/* Products Produced */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Package className="w-6 h-6 text-orange-600 dark:text-orange-300" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('operator.products')}</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {currentStats.products_produced}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('operator.differentProducts')}
          </p>
        </div>

        {/* Personal Best */}
        {bestSession && (
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900 dark:to-yellow-950 rounded-lg shadow-md p-6 border-2 border-yellow-300 dark:border-yellow-700">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-200 dark:bg-yellow-800 rounded-lg">
                <Award className="w-6 h-6 text-yellow-700 dark:text-yellow-300" />
              </div>
              <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">{t('operator.personalBest')}</span>
            </div>
            <div className="text-3xl font-bold text-yellow-900 dark:text-yellow-100 mb-1">
              {formatYield(Number(bestSession.yield_percentage || 0))}
            </div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              {bestSession.product_name}
            </p>
          </div>
        )}
      </div>

      {/* Recent Sessions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-gray-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('operator.recentSessions')}
            </h2>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.session')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.product')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.station')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.output')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.waste')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.yield')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.duration')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('operator.status')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {recentSessions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('operator.noSessionsFound')}
                  </td>
                </tr>
              ) : (
                recentSessions.map((session) => (
                  <tr key={session.session_number} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {session.session_number}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(session.started_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {session.product_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {session.product_sku}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {session.station_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatWeight(session.actual_output_weight)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatWeight(session.waste_weight)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <TrendingUp
                          className={`w-4 h-4 mr-1 ${getYieldIconColor(Number(session.yield_percentage || 0))}`}
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatYield(Number(session.yield_percentage || 0))}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {session.started_at && session.completed_at
                        ? calculateSessionDuration(session.started_at, session.completed_at)
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(session.status)}`}>
                        {t(`status.${session.status}`) || session.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
